import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle, Download, Mail, FileText, Calendar, Loader2 } from 'lucide-react';
import { useAgreement } from '../context/AgreementContext';
import { downloadSignedAgreementPdf, downloadXeroInvoicePdf } from '../services/agreementService';

export function ConfirmationScreen() {
  const { state } = useAgreement();
  const { agreement_id } = useParams<{ agreement_id: string }>();
  const [downloadingAgreement, setDownloadingAgreement] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Access data from multiple possible paths to handle both context and backend data structures
  const primaryContact = (state as any).purchaser_details?.individualDetails?.[0] || state.individualDetails?.[0];
  const purchaserType = (state as any).purchaser_details?.purchaserType || state.purchaserType;
  const maxPrice = (state as any).purchaser_details?.agreementDetails?.preferences?.maxPrice ||
                   (state as any).purchaser_details?.preferences?.maxPrice ||
                   state.preferences?.maxPrice;
  
  const getPrimaryContactName = () => {
    if (primaryContact?.firstName && primaryContact?.lastName) {
      return [primaryContact.firstName, primaryContact.middleName, primaryContact.lastName]
        .filter(Boolean)
        .join(' ');
    }
    return 'N/A';
  };

  // Download handlers
  const handleDownloadAgreement = async () => {
    if (!agreement_id || downloadingAgreement) return;
    
    setDownloadingAgreement(true);
    try {
      console.log('Downloading signed agreement PDF for agreement:', agreement_id);
      await downloadSignedAgreementPdf(agreement_id);
    } catch (error) {
      console.error('Error downloading agreement:', error);
      // You could add a toast notification here to inform the user
    } finally {
      setDownloadingAgreement(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!agreement_id || downloadingReceipt) return;
    
    setDownloadingReceipt(true);
    try {
      console.log('Downloading Xero invoice PDF for agreement:', agreement_id);
      await downloadXeroInvoicePdf(agreement_id);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      // You could add a toast notification here to inform the user
    } finally {
      setDownloadingReceipt(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <CheckCircle className="w-10 h-10 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Agreement Successfully Submitted!
          </h1>
          <p className="text-lg text-gray-600">
            Welcome to Search Property - your property search journey begins now
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Confirmation Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Agreement Summary
            </h2>

            <div className="space-y-4">
              <div className="flex justify-between items-start py-2 border-b border-gray-200">
                <span className="text-gray-600">Agreement ID</span>
                <span className="font-medium text-gray-900 text-right break-all max-w-[60%]">{state.id || 'N/A'}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-600">Date Signed</span>
                <span className="font-medium text-gray-900">
                  {new Date().toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-600">Purchaser Type</span>
                <span className="font-medium text-gray-900 capitalize">
                  {purchaserType || 'N/A'}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-600">Budget Range</span>
                <span className="font-medium text-gray-900">
                  {maxPrice ? `Up to ${formatPrice(maxPrice)}` : 'N/A'}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-600">Retainer Paid</span>
                <span className="font-medium text-green-600">
                  {state.total_retainer ? `${formatPrice(state.total_retainer)} + GST ✓` : 'N/A'}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Primary Contact</span>
                <span className="font-medium text-gray-900">
                  {getPrimaryContactName()}
                </span>
              </div>
            </div>
          </div>

          {/* Download Options */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Your Documents
            </h2>

            <div className="space-y-4">
              <button
                onClick={handleDownloadAgreement}
                disabled={downloadingAgreement}
                className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="bg-primary-100 p-3 rounded-lg">
                  <FileText className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-medium text-gray-900">Signed Agreement</h3>
                  <p className="text-sm text-gray-600">Complete agreement with all signatures</p>
                </div>
                {downloadingAgreement ? (
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 text-gray-400" />
                )}
              </button>

              <button
                onClick={handleDownloadReceipt}
                disabled={downloadingReceipt}
                className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="bg-primary-100 p-3 rounded-lg">
                  <FileText className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-medium text-gray-900">Payment Receipt</h3>
                  <p className="text-sm text-gray-600">Receipt for retainer fee payment</p>
                </div>
                {downloadingReceipt ? (
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>

            <div className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-primary-900 mb-1">Email Confirmation Sent</h4>
                  <p className="text-sm text-primary-800">
                    Copies of all documents have been sent to {primaryContact?.email || 'your email address'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            What Happens Next?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Calendar className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Within 24 Hours</h3>
              <p className="text-sm text-gray-600">
                Your dedicated property specialist will contact you to discuss your requirements in detail and begin the search process.
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Weekly Updates</h3>
              <p className="text-sm text-gray-600">
                Receive detailed market reports and property opportunities that match your investment criteria and preferences.
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Property Match</h3>
              <p className="text-sm text-gray-600">
                When we find the perfect property, we'll arrange inspections and guide you through the entire purchase process.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
       <div className="bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-xl p-6 md:p-8 mt-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">
              Questions? We're Here to Help
            </h2>
           <p className="text-primary-100 mb-6">
              Our team is available to assist you throughout your property search journey
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:1300123123"
               className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
              >
                Call 1300 123 123
              </a>
              <a
                href="mailto:support@searchproperty.com.au"
               className="inline-flex items-center justify-center px-6 py-3 bg-primary-700 text-white rounded-lg font-semibold hover:bg-primary-800 transition-colors"
              >
                Email Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}